import { Link, useSearchParams } from 'react-router-dom'
import { monthName, toDate, toISODate, weekdayName, type ISODate } from '@app/shared'

import { useClock, useJournalEntries } from '../../lib/data'
import { MChevronLeft, MChevronRight, MJournal } from '../shell/icons'
import { Band, BandHeading, BandTile, Dot, ReadFailure, ScrollColumn, Skeleton } from '../shell/parts'

// ============================================================================
// READING BACK, ON THE PHONE — JRN-1c.
//
// WHY THIS EXISTS AT ALL, given that the mobile README puts "journal index and
// day pages" on the desktop. The rule it was written to protect is that the
// phone does not become a second place to BROWSE; the thing the owner actually
// hit is narrower and is not browsing: he journals daily on the phone, and had
// no way to read or fix yesterday from it. So this is one month at a time,
// newest first, and it is a list of days — not a search, not a heat map, not a
// tag cloud. Those are still the desktop's, and DesktopOnly still says so about
// everything else.
//
// ONE READ. `useJournalEntries({from,to})` is `GET /api/journal/entries?from&to`,
// which answers per-day SUMMARIES: the text on each row is a 180-character
// preview the Worker cut, not the entry. That is why a row links to the day page
// instead of expanding — the full words genuinely are not here, and pretending
// otherwise would be the app showing a truncated entry as if it were the whole
// one. `remote.ts` maps `preview` onto `text`, so this file never sees the
// difference and must never assume it has everything.
//
// A DAY WITH NO ENTRY IS NOT A ROW. The API answers with the days that exist;
// empty days are absent, and drawing a placeholder for each would turn a month
// of honest silence into 30 rows of nothing.
// ============================================================================

/** The first of the month a date falls in. */
function monthStart(date: ISODate): ISODate {
  return `${date.slice(0, 7)}-01` as ISODate
}

/** The last day of that month — day 0 of the next one, at the noon-UTC pin `toDate` uses. */
function monthEnd(date: ISODate): ISODate {
  const d = toDate(date)
  return toISODate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 12)))
}

/** The same day-of-month is not safe (the 31st of a 30-day month); the FIRST always is. */
function shiftMonth(date: ISODate, by: number): ISODate {
  const d = toDate(date)
  return toISODate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + by, 1, 12)))
}

function monthLabel(date: ISODate): string {
  return `${monthName(date)} ${date.slice(0, 4)}`
}

export function MobileJournalArchive() {
  const { data: clock } = useClock()
  // THE MONTH LIVES IN THE URL, not in a `useState`. Opening a day unmounts this screen (the
  // route tree is keyed by tab), so component state would put the owner back in the current
  // month every time he read one entry and came back. `?month=` survives that, survives a
  // reload, and is the address the day page hands back on its way out.
  //
  // It starts on the month the app's own clock is in — never `new Date()`, which would drift
  // from demo mode's fixed day and make every screenshot a different one.
  const [params, setParams] = useSearchParams()
  const asked = params.get('month')
  const month =
    asked && /^\d{4}-\d{2}-\d{2}$/.test(asked)
      ? monthStart(asked as ISODate)
      : clock
        ? monthStart(clock.date)
        : null

  // `?tag=` is the desktop journal's own link shape, and honouring it here costs one filter
  // because every row already carries its tags. WHAT IT DOES NOT DO is search: this filters the
  // month on screen, while the desktop's tag view spans the whole journal. A phone that quietly
  // showed one month's worth and looked like all of it would be the app claiming to have looked
  // everywhere, so the heading and the empty state both say which month was actually read.
  const tag = params.get('tag')

  const goToMonth = (next: ISODate) =>
    setParams(tag ? { month: next, tag } : { month: next }, { replace: true })

  const range = month ? { from: month, to: monthEnd(month) } : undefined
  const { data: entries, isPending, error } = useJournalEntries(range)

  // Newest first: reading back means starting at the last thing written.
  const rows = [...(entries ?? [])]
    .filter((entry) => !tag || entry.tags.includes(tag))
    .sort((a, b) => (a.date < b.date ? 1 : -1))
  // There are no entries after today, so there is nothing to page forward into.
  const atCurrentMonth = Boolean(clock && month && month === monthStart(clock.date))

  return (
    <>
      <Band band="today">
        <BandHeading
          title={month ? monthLabel(month) : '…'}
          action={
            <div className="flex flex-none items-center gap-2">
              <BandTile
                label="Previous month"
                onClick={() => month && goToMonth(shiftMonth(month, -1))}
              >
                <MChevronLeft />
              </BandTile>
              {/* FORWARD IS NOT DRAWN ON THE CURRENT MONTH rather than drawn and disabled: a
                  control that cannot do anything is an affordance that does nothing, and this
                  product does not draw those. */}
              {!atCurrentMonth && (
                <BandTile label="Next month" onClick={() => month && goToMonth(shiftMonth(month, 1))}>
                  <MChevronRight />
                </BandTile>
              )}
            </div>
          }
          subline={
            <>
              <span className="font-medium">Journal history</span>
              {!isPending && !error && (
                <>
                  <Dot />
                  <span>
                    {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
                  </span>
                </>
              )}
              {tag && (
                <>
                  <Dot />
                  {/* Tapping it clears the filter — the only way back to the whole month. */}
                  <Link
                    to={month ? `/journal?month=${month}` : '/journal'}
                    aria-label={`Stop filtering by #${tag}`}
                    className="rounded-pill bg-surface/80 px-2 py-[3px] font-medium text-ink-2"
                  >
                    #{tag} ×
                  </Link>
                </>
              )}
            </>
          }
        />
      </Band>

      <ScrollColumn className="pt-[2px]">
        {isPending && (
          <>
            <Skeleton height={84} className="rounded-context" />
            <Skeleton height={84} className="rounded-context" />
            <Skeleton height={84} className="rounded-context" />
          </>
        )}

        {/* A failed read is not an empty month. */}
        {error && <ReadFailure error={error} what="The journal" />}

        {!isPending && !error && rows.length === 0 && (
          <p className="px-1 pt-2 text-[12.5px] leading-[1.5] text-microcopy text-pretty">
            {tag ? (
              <>
                No entry in {month ? monthLabel(month) : 'this month'} carries #{tag}. Only that
                month was read — this list filters the month on screen and does not search the
                other ones.
              </>
            ) : (
              <>
                Nothing was written in {month ? monthLabel(month) : 'this month'}. Days with no
                entry are not listed — this month simply has none.
              </>
            )}
          </p>
        )}

        {/* TODAY'S PAGE, WHEN IT HAS NO ENTRY YET. The composer left Day in V10 (owner,
            2026-10-18: the journal lives in Notes), so until Phase 4's Notes | Journal switch this
            list is the way to today's page — and a day nobody has written yet is not in it. The
            row is the same shape as a written day's, and says plainly that nothing is written. */}
        {clock && atCurrentMonth && !tag && !isPending && !error && !rows.some((r) => r.date === clock.date) && (
          <Link
            to={month ? `/journal/${clock.date}?from=${month}` : `/journal/${clock.date}`}
            className="flex flex-none items-center gap-[9px] rounded-context border border-line bg-surface px-4 py-[13px] active:bg-fill"
          >
            <MJournal className="flex-none text-journal" />
            <span className="text-[12.5px] font-semibold leading-none text-ink-2">Write today’s entry</span>
            <span className="rounded-pill bg-fill px-2 py-[3px] text-[10px] font-semibold leading-[1.4] text-muted">
              Today
            </span>
            <span className="ml-auto flex-none text-[11px] font-medium leading-none text-muted">nothing written yet</span>
          </Link>
        )}

        {rows.map((entry) => (
          <DayRow key={entry.date} entry={entry} today={clock?.date} month={month} tag={tag} />
        ))}
      </ScrollColumn>
    </>
  )
}

/**
 * One day. The whole row is the tap target (a phone has no hover to reveal one),
 * and it carries only what the summary read actually answered.
 */
function DayRow({
  entry,
  today,
  month,
  tag,
}: {
  entry: {
    date: ISODate
    text: string
    wordCount: number
    mood: number | null
    tags: string[]
    voiceMemoCount: number
  }
  today: ISODate | undefined
  /** Carried so the day page's back control returns to the month being read, not to this one. */
  month: ISODate | null
  /** Carried for the same reason: Back should land on the filtered list, not the whole month. */
  tag: string | null
}) {
  const day = toDate(entry.date).getUTCDate()
  return (
    <Link
      to={
        month
          ? `/journal/${entry.date}?from=${month}${tag ? `&tag=${encodeURIComponent(tag)}` : ''}`
          : `/journal/${entry.date}`
      }
      className="flex flex-none flex-col gap-[7px] rounded-context border border-line bg-surface px-4 py-[13px] active:bg-fill"
    >
      <div className="flex items-center gap-[9px]">
        <MJournal className="flex-none text-journal" />
        <span className="text-[12.5px] font-semibold leading-none text-ink-2">
          {weekdayName(entry.date, true)} {day}
        </span>
        {today === entry.date && (
          <span className="rounded-pill bg-fill px-2 py-[3px] text-[10px] font-semibold leading-[1.4] text-muted">
            Today
          </span>
        )}
        <span className="ml-auto flex-none text-[11px] font-medium leading-none text-muted">
          {entry.wordCount} {entry.wordCount === 1 ? 'word' : 'words'}
        </span>
      </div>

      {/* THE PREVIEW, AND IT IS LABELLED BY ITS SHAPE, NOT BY A CLAIM: two lines, clamped, and
          the day page is one tap away for the rest. `line-clamp` rather than a JS slice — the
          server already cut this string at 180 characters and cutting it twice would lose words
          for no reason. */}
      {entry.text.length > 0 && (
        <p className="line-clamp-2 text-[12.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
          {entry.text}
        </p>
      )}

      {(entry.mood !== null || entry.tags.length > 0 || entry.voiceMemoCount > 0) && (
        <div className="flex items-center gap-[7px] text-[11px] font-medium leading-none text-muted">
          {entry.mood !== null && <span>Mood {entry.mood}</span>}
          {entry.mood !== null && entry.tags.length > 0 && <Dot />}
          {entry.tags.length > 0 && (
            <span className="truncate">{entry.tags.map((t) => `#${t}`).join(' · ')}</span>
          )}
          {entry.voiceMemoCount > 0 && (entry.mood !== null || entry.tags.length > 0) && <Dot />}
          {entry.voiceMemoCount > 0 && (
            <span>
              {entry.voiceMemoCount} {entry.voiceMemoCount === 1 ? 'memo' : 'memos'}
            </span>
          )}
        </div>
      )}
    </Link>
  )
}
